import { TypeAsserter } from '@attain-apps/type-guards'
import { format } from 'pretty-format'

import * as definitions from './definitions'
import type { Fn } from './fn'
import type { FnHelper } from './helper'
import { Key } from './key'

/**
 * Just a helper type to represent all {@link Fn} in a single interface
 */
export type AllFn<T = any> = InterfaceToType<
  Satisfies<
    definitions.Add<T> &
      definitions.And<T> &
      definitions.Coalesce<T> &
      definitions.Concatenate<T> &
      definitions.Count<T> &
      definitions.CreateObject<T> &
      definitions.CreateTuple<T> &
      definitions.Divide<T> &
      definitions.Equals<T> &
      definitions.Every<T> &
      definitions.Exponent<T> &
      definitions.FormatNumber<T> &
      definitions.GetAncestorId<T> &
      definitions.GetAttribute<T> &
      definitions.GetModelInfo<T> &
      definitions.GetProperty<T> &
      definitions.GetRecord<T> &
      definitions.GetSubquery<T> &
      definitions.GetVariable<T> &
      definitions.GreaterThan<T> &
      definitions.GreaterThanOrEqualTo<T> &
      definitions.If<T> &
      definitions.In<T> &
      definitions.Inject<T> &
      definitions.IsSimilar<T> &
      definitions.LessThan<T> &
      definitions.LessThanOrEqualTo<T> &
      definitions.ListToObject<T> &
      definitions.Map<T> &
      definitions.Max<T> &
      definitions.Min<T> &
      definitions.Modulo<T> &
      definitions.Multiply<T> &
      definitions.Not<T> &
      definitions.NotEquals<T> &
      definitions.ObjectToList<T> &
      definitions.Or<T> &
      definitions.Score<T> &
      definitions.Similarity<T> &
      definitions.Some<T> &
      definitions.Subtract<T> &
      definitions.Unique<T>,
    // NOTE: ensures we don't forget any Fns
    Record<Key, unknown>
  >
>

// NOTE: this type could be more specific, but that causes problems when it is
// being consumed
export const HELPERS_LOOKUP = {
  // NOTE: we use getters since there are circular dependencies, so we need to
  // lazily retrieve each HELPER
  /* eslint-disable jsdoc/require-jsdoc */
  get [Key.Add]() {
    return definitions.Add.HELPER
  },
  get [Key.And]() {
    return definitions.And.HELPER
  },
  get [Key.Coalesce]() {
    return definitions.Coalesce.HELPER
  },
  get [Key.Concatenate]() {
    return definitions.Concatenate.HELPER
  },
  get [Key.Count]() {
    return definitions.Count.HELPER
  },
  get [Key.CreateObject]() {
    return definitions.CreateObject.HELPER
  },
  get [Key.CreateTuple]() {
    return definitions.CreateTuple.HELPER
  },
  get [Key.Divide]() {
    return definitions.Divide.HELPER
  },
  get [Key.Equals]() {
    return definitions.Equals.HELPER
  },
  get [Key.Every]() {
    return definitions.Every.HELPER
  },
  get [Key.Exponent]() {
    return definitions.Exponent.HELPER
  },
  get [Key.FormatNumber]() {
    return definitions.FormatNumber.HELPER
  },
  get [Key.GetAncestorId]() {
    return definitions.GetAncestorId.HELPER
  },
  get [Key.GetAttribute]() {
    return definitions.GetAttribute.HELPER
  },
  get [Key.GetModelInfo]() {
    return definitions.GetModelInfo.HELPER
  },
  get [Key.GetProperty]() {
    return definitions.GetProperty.HELPER
  },
  get [Key.GetRecord]() {
    return definitions.GetRecord.HELPER
  },
  get [Key.GetSubquery]() {
    return definitions.GetSubquery.HELPER
  },
  get [Key.GetVariable]() {
    return definitions.GetVariable.HELPER
  },
  get [Key.GreaterThan]() {
    return definitions.GreaterThan.HELPER
  },
  get [Key.GreaterThanOrEqualTo]() {
    return definitions.GreaterThanOrEqualTo.HELPER
  },
  get [Key.If]() {
    return definitions.If.HELPER
  },
  get [Key.In]() {
    return definitions.In.HELPER
  },
  get [Key.Inject]() {
    return definitions.Inject.HELPER
  },
  get [Key.LessThan]() {
    return definitions.LessThan.HELPER
  },
  get [Key.LessThanOrEqualTo]() {
    return definitions.LessThanOrEqualTo.HELPER
  },
  get [Key.ListToObject]() {
    return definitions.ListToObject.HELPER
  },
  get [Key.Map]() {
    return definitions.Map.HELPER
  },
  get [Key.Max]() {
    return definitions.Max.HELPER
  },
  get [Key.Min]() {
    return definitions.Min.HELPER
  },
  get [Key.Modulo]() {
    return definitions.Modulo.HELPER
  },
  get [Key.Multiply]() {
    return definitions.Multiply.HELPER
  },
  get [Key.Not]() {
    return definitions.Not.HELPER
  },
  get [Key.NotEquals]() {
    return definitions.NotEquals.HELPER
  },
  get [Key.ObjectToList]() {
    return definitions.ObjectToList.HELPER
  },
  get [Key.Or]() {
    return definitions.Or.HELPER
  },
  get [Key.Score]() {
    return definitions.Score.HELPER
  },
  get [Key.Some]() {
    return definitions.Some.HELPER
  },
  get [Key.Subtract]() {
    return definitions.Subtract.HELPER
  },
  get [Key.Unique]() {
    return definitions.Unique.HELPER
  },
  get [Key.Similarity]() {
    return definitions.Similarity.HELPER
  },
  get [Key.IsSimilar]() {
    return definitions.IsSimilar.HELPER
  },
  /* eslint-enable jsdoc/require-jsdoc */
} satisfies {
  [K in Key]: FnHelper<Pick<AllFn, K>>
} as Readonly<Record<Key, FnHelper<Fn>>>

export function getHelper<TFn extends Fn>(fn: TFn): FnHelper<TFn> {
  const key = Key.from(fn)
  TypeAsserter.assertIsNotNullish(
    key,
    `DMS function is not recognized: ${format(fn)}`,
  )
  // NOTE: casting is needed because TFn is invariant for FnHelper
  return HELPERS_LOOKUP[key] satisfies FnHelper<Fn> as any as FnHelper<TFn>
}

let helpers: readonly FnHelper<Fn>[] | undefined
export function getAllHelpers(): readonly FnHelper<Fn>[] {
  return (helpers ??= Object.values(HELPERS_LOOKUP))
}
